import { useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

const TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const WARNING_MS = 2 * 60 * 1000; // warn 2 minutes before
const ACTIVITY_EVENTS = ["mousedown", "keydown", "scroll", "touchstart", "mousemove"] as const;

export function useSessionTimeout() {
  const { session, signOut } = useAuth();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasWarnedRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
  }, []);

  const handleTimeout = useCallback(async () => {
    toast.info("Session expired due to inactivity. Please sign in again.");
    await signOut();
  }, [signOut]);

  const resetTimers = useCallback(() => {
    if (!session) return;
    clearTimers();
    hasWarnedRef.current = false;

    warningRef.current = setTimeout(() => {
      if (!hasWarnedRef.current) {
        hasWarnedRef.current = true;
        toast.warning("Your session will expire in 2 minutes due to inactivity.", {
          duration: 10000,
        });
      }
    }, TIMEOUT_MS - WARNING_MS);

    timeoutRef.current = setTimeout(handleTimeout, TIMEOUT_MS);
  }, [session, clearTimers, handleTimeout]);

  useEffect(() => {
    if (!session) {
      clearTimers();
      return;
    }

    resetTimers();

    const onActivity = () => resetTimers();

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, onActivity, { passive: true });
    }

    return () => {
      clearTimers();
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, onActivity);
      }
    };
  }, [session, resetTimers, clearTimers]);
}
